/**
 * @fileoverview Feature Cards Section - Product benefits
 *
 * Purpose: Display key product benefits on a light section, each cell
 * delineated by divider lines (Linear / CommodityAI style) rather than
 * bordered cards.
 *
 * Layout:
 * - Two-column header: serif heading (primary green) on the left, primary green
 *   "See Morgan in action" CTA on the right (opens the demo request dialog).
 * - 2x2 grid separated by a single vertical and single horizontal divider line.
 *   Each cell shows heading + supporting copy above a lighter-green container
 *   reserved for an animation.
 *
 * Note: Animation components are intentionally not wired in yet — each cell's
 * lighter-green container is left clear so an animation can be dropped in later.
 *
 * Theming: uses the global semantic CSS variables so the section adapts to
 * light and dark mode (the marketing footer exposes a theme switcher).
 *
 * Used by: Homepage (index.tsx)
 */

import { useEffect, useRef, useState } from "react";
import { Button } from "@/ui/primitives";
import { ArrowRight } from "lucide-react";
import { cn } from "@/utils/misc";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";
import {
  VoidableTransactionsAnimation,
  VOIDABLE_ANIMATION_CYCLE_MS,
} from "./-ui.voidable-transactions-animation";
import {
  SecuredCreditorAnimation,
  SECURED_CREDITOR_ANIMATION_CYCLE_MS,
} from "./-ui.secured-creditor-animation";
import {
  ReportOutlineAnimation,
  REPORT_OUTLINE_ANIMATION_CYCLE_MS,
} from "./-ui.report-outline-animation";
import {
  DeadlineCalendarAnimation,
  DEADLINE_CALENDAR_ANIMATION_CYCLE_MS,
} from "./-ui.deadline-calendar-animation";

/**
 * Card indices that currently have an animation. The orchestrator cycles
 * through these so only one plays at a time; the active card shows a progress
 * line while it runs. Add indices here as more card animations are built.
 */
const ANIMATED_CARDS = [0, 1, 2, 3];

/**
 * Per-card play-through duration. The orchestrator holds each card active for
 * its own cycle length so every animation runs end-to-end before handing off.
 */
const CARD_CYCLE_MS: Record<number, number> = {
  0: VOIDABLE_ANIMATION_CYCLE_MS,
  1: SECURED_CREDITOR_ANIMATION_CYCLE_MS,
  2: REPORT_OUTLINE_ANIMATION_CYCLE_MS,
  3: DEADLINE_CALENDAR_ANIMATION_CYCLE_MS,
};

/**
 * Feature cards content.
 *
 * Each entry renders a card with a clear animation placeholder above its
 * heading and supporting copy.
 */
const FEATURE_CARDS = [
  {
    title: "Automated investigations with practitioners\nin the loop.",
    description:
      "Months of work in minutes. Go deeper on insolvency analysis, voidable transactions, civil & criminal claims, all sourced to original data.",
  },
  {
    title: "Reports, Day Ones, & creditor communications, drafted in seconds.",
    description:
      "Draft reports, forms & file notes in one click. Automate Day Ones, ongoing comms & reporting with AI (with all responses captured as matter data).",
  },
  {
    title: "From raw data (in any form) to clear decisions in minutes.",
    description:
      "From what's happened to what's happening now — your view of estimated outcomes, creditors, & assets updates as the data changes.",
  },
  {
    title: "Guided workflows, aligned team, zero missed deadlines.",
    description:
      "Deadlines automatically added to your calendar, your CRM populates from job data, & Morgan guides the team through every task.",
  },
] as const;

/**
 * Feature cards showcasing product benefits.
 */
export function FeatureCardsSection() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  // Gate the animation orchestrator on scroll: nothing plays until the section
  // scrolls into view, so the first (voidable transactions) animation kicks off
  // exactly when the user reaches this section rather than on initial mount.
  const sectionRef = useRef<HTMLElement | null>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      // Fallback for environments without IntersectionObserver: play immediately.
      setHasEnteredView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasEnteredView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Orchestrator: cycle the active animation through ANIMATED_CARDS so only one
  // plays at a time. Each card is held active for its own cycle length (via a
  // self-rearming timeout) so every animation runs end-to-end before handing
  // off to the next. Starts from the first card once the section is in view.
  // On mobile the cards are stacked, so only one is on screen at a time — the
  // global one-at-a-time orchestrator would leave the visible card idle. Instead,
  // each card plays when it scrolls into view. (Desktop keeps the orchestrator,
  // since all four cards are visible together in the 2x2 grid.)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const [activeAnimated, setActiveAnimated] = useState(0);
  const activeCardIndex = ANIMATED_CARDS[activeAnimated];

  // Desktop: hovering a card plays its animation immediately and pauses the
  // auto-cycle, so the user can dwell on whichever card they're inspecting.
  // The cycle resumes from where it left off once the pointer leaves.
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    if (!hasEnteredView) return;
    if (ANIMATED_CARDS.length <= 1) return;
    // Pause the orchestrator while a card is hovered (desktop only).
    if (!isMobile && hoveredCard !== null) return;
    const duration =
      CARD_CYCLE_MS[activeCardIndex] ?? VOIDABLE_ANIMATION_CYCLE_MS;
    const id = setTimeout(
      () => setActiveAnimated((a) => (a + 1) % ANIMATED_CARDS.length),
      duration,
    );
    return () => clearTimeout(id);
  }, [hasEnteredView, activeAnimated, activeCardIndex, hoveredCard, isMobile]);

  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [cardInView, setCardInView] = useState<boolean[]>(() =>
    FEATURE_CARDS.map(() => false),
  );
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setCardInView(FEATURE_CARDS.map(() => true));
      return;
    }
    const observers = cardRefs.current.map((node, i) => {
      if (!node) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          const isIn = entries.some((entry) => entry.isIntersecting);
          setCardInView((prev) => {
            if (prev[i] === isIn) return prev;
            const next = [...prev];
            next[i] = isIn;
            return next;
          });
        },
        { threshold: 0.4 },
      );
      observer.observe(node);
      return observer;
    });
    return () => observers.forEach((observer) => observer?.disconnect());
  }, []);

  const isCardActive = (index: number) =>
    isMobile
      ? cardInView[index]
      : // Desktop: a hovered card always plays; otherwise the auto-cycle drives
        // which card is active.
        hoveredCard === index ||
        (hoveredCard === null && hasEnteredView && activeCardIndex === index);

  return (
    <>
      <section
        ref={sectionRef}
        className="relative w-full border-t border-primary/10 bg-background py-8 text-foreground md:py-24"
      >
        <div className="mx-auto max-w-7xl px-6">
          {/* Header: heading left, centered CTA right */}
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-16">
            <div>
              <h2 className="max-w-xl text-3xl font-medium leading-[1.1] text-primary md:text-[42px]">
                Reduce write-offs.
                <br />
                Preserve value.
              </h2>
            </div>

            <div className="flex justify-start md:justify-end">
              <Button
                variant="default"
                size="lg"
                expandIcon
                className="gap-2 text-base"
                onClick={() => setIsDemoDialogOpen(true)}
              >
                See Morgan in action
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* 2x2 grid delineated by divider lines (one vertical, one horizontal) */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2">
            {FEATURE_CARDS.map((card, index) => {
              const isLeftCol = index % 2 === 0;
              const isTopRow = index < 2;
              return (
                <div
                  key={card.title}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() =>
                    setHoveredCard((current) =>
                      current === index ? null : current,
                    )
                  }
                  className={cn(
                    "flex min-h-[460px] flex-col border-primary/10 py-6 md:min-h-[560px] md:py-12",
                    // Top row sits right below the header, so trim its top padding
                    isTopRow && "pt-4",
                    // Desktop: single horizontal line under the top row only
                    // (no dividers between the vertically-stacked cells on mobile)
                    isTopRow && "md:border-b",
                    // Desktop: single vertical line between the two columns
                    isLeftCol ? "md:border-r md:pr-12" : "md:pl-12",
                  )}
                >
                  {/* Lighter-green animation container — drop an animation in here.
                      min-h keeps the animation a consistent height across
                      breakpoints (on mobile the wrapping heading would otherwise
                      shrink this flex-1 box and clip the animation). */}
                  <div
                    ref={(el) => {
                      cardRefs.current[index] = el;
                    }}
                    className="relative order-2 min-h-[330px] flex-1 overflow-hidden rounded-lg border border-primary/10 bg-primary/[0.06] md:order-1"
                    aria-hidden="true"
                  >
                    {index === 0 && (
                      <VoidableTransactionsAnimation
                        isActive={isCardActive(index)}
                      />
                    )}
                    {index === 1 && (
                      <SecuredCreditorAnimation
                        isActive={isCardActive(index)}
                      />
                    )}
                    {index === 2 && (
                      <ReportOutlineAnimation isActive={isCardActive(index)} />
                    )}
                    {index === 3 && (
                      <DeadlineCalendarAnimation
                        isActive={isCardActive(index)}
                      />
                    )}
                  </div>

                  <div className="order-1 mb-6 md:order-2 md:mb-0">
                    <h3 className="min-h-[66px] whitespace-pre-line text-2xl font-semibold leading-snug text-primary md:mt-8">
                      {card.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-foreground/60">
                      {card.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </>
  );
}
